import { FC, useCallback, useRef } from "react";
import { MenuProps } from "react-select";

import { AdjustmentBounds, ElementBounds, Overlay } from "../../../../general";
import { createComponentStyleHook } from "../../../../tokens";
import classNames from "classnames";

import { Bounds } from "../../../../utilities/BoundsObserver/Bounds";
import { ValidityGroup } from "../../../ValidityGroup";
import { getMenuAdjustment } from "../../helpers";
import { OptionItem } from "../../OptionItem";

import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

const useResolvedDropdownMenuStyles = createComponentStyleHook<MenuProps<OptionItem | null>>({
  legacyStyles,
  tokenStyles,
  componentTokens: "dropdown_menu",
});

/**
 * The menu component.
 */
export const Menu: FC<MenuProps<OptionItem | null>> = (props) => {
  const { children, innerProps, innerRef } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedDropdownMenuStyles(props);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);

  const menuClassName = classNames(styles.Menu, "menu");

  const adjustOverlay = useCallback((elementBounds: ElementBounds, containerBounds: Bounds): AdjustmentBounds => {
    const regions: Bounds[] = [containerBounds];

    // The overlay container spans the viewport, so a menu inside a modal or drawer must also respect that dialog's edges.
    const dialogBounds = menuContainerRef.current?.closest("dialog")?.getBoundingClientRect();
    if (dialogBounds) {
      const { top, left, width, height } = dialogBounds;
      regions.push({ top, left, width, height });
    }

    return getMenuAdjustment(elementBounds.bounds, menuContainerRef.current?.offsetTop ?? 0, regions);
  }, []);

  return (
    <StyleModeBoundary mode={mode}>
      <div ref={menuContainerRef} className={styles.MenuContainer}>
        <Overlay adjust={adjustOverlay}>
          <div {...innerProps} ref={innerRef} className={menuClassName} role="presentation">
            <ValidityGroup ignoreParent invalid={false} error={null}>
              {children}
            </ValidityGroup>
          </div>
        </Overlay>
      </div>
    </StyleModeBoundary>
  );
};
